import React, { useEffect, useRef, useState } from "react";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import "./TenderPriceFeedback.css";

const positiveId = (value) => Number(value) > 0 ? Number(value) : null;
const lineKey = (line, index) => String(line._clientKey || line._destinationKey || line.id || `line-${index}`);
const PRICE_SNAPSHOT_FIELDS = ["tender_id", "price_source", "pricing_date", "tender_unit_price", "tender_currency", "tender_starts_on", "tender_ends_on"];

export function purchasePriceSnapshot(value = {}) {
  return Object.fromEntries(PRICE_SNAPSHOT_FIELDS
    .filter((key) => value[key] !== undefined).map((key) => [key, value[key]]));
}

// Prices and contract snapshots belong to an item, not to the reusable editor row.
export function clearedPurchasePrice() {
  return { ...Object.fromEntries(PRICE_SNAPSHOT_FIELDS.map((key) => [key, null])),
    unit_price: "", last_price_info: null, total_price: 0 };
}

export function purchasePriceRequest(form, documentType) {
  return {
    document_type: documentType,
    date: String((documentType === "order" ? form?.order_date : form?.request_date) || "").slice(0, 10),
    currency: form?.currency || "EUR",
    lines: (form?.lines || []).filter((line) => positiveId(line.item_id)).map((line, index) => ({
      client_key: lineKey(line, index),
      item_id: positiveId(line.item_id),
      supplier_id: positiveId(documentType === "order" ? form.supplier_id : line.supplier_id),
      unit_id: positiveId(line.unit_id),
    })),
  };
}

export function applyPurchasePriceResults(form, results, documentType, originalPrices, repricedKeys = new Set()) {
  const prices = new Map((results || []).map((entry) => [String(entry.client_key), entry]));
  let changed = false;
  let pricedIndex = 0;
  const lines = (form.lines || []).map((line) => {
    if (!positiveId(line.item_id)) return line;
    const result = prices.get(lineKey(line, pricedIndex++));
    const supplierId = positiveId(documentType === "order" ? form.supplier_id : line.supplier_id);
    if (!result || Number(result.item_id) !== Number(line.item_id) || positiveId(result.supplier_id) !== supplierId) return line;
    if (result.source !== "tender" && !line.tender_id && originalPrices &&
        Number(line.unit_price) !== Number(originalPrices.get(String(result.client_key)))) return line;
    if (result.source !== "tender" && !line.tender_id && Number(line.unit_price) > 0 &&
        (line.id || line.price_source === "manual") && !repricedKeys.has(String(result.client_key))) return line;
    const price = Number(result.unit_price);
    if (!Number.isFinite(price) || (result.source === "none" && !line.tender_id)) return line;
    const snapshot = {
      tender_id: result.tender_id || null,
      price_source: result.price_source || result.source,
      pricing_date: result.pricing_date || null,
      tender_unit_price: result.tender_unit_price ?? null,
      tender_currency: result.tender_currency || null,
      tender_starts_on: result.tender_starts_on || null,
      tender_ends_on: result.tender_ends_on || null,
    };
    if (Number(line.unit_price) === price && Object.entries(snapshot).every(([key, value]) => (line[key] ?? null) === value) &&
        (documentType !== "requisition" || Number(line.last_price_info?.last_price) === price)) return line;
    changed = true;
    return {
      ...line, ...snapshot, unit_price: price, total_price: Number(line.quantity || 0) * price,
      ...(documentType === "requisition" ? { last_price_info: { ...line.last_price_info, ...result, last_price: price } } : {}),
    };
  });
  return changed ? { ...form, lines } : form;
}

// Resolve only commercial documents. No receiving or delivery-note values are written here.
export function usePurchaseTenderPrices({ form, setForm, documentType, enabled, businessUnitId }) {
  const latestForm = useRef(form);
  const previousContext = useRef(null);
  latestForm.current = form;
  const requestKey = JSON.stringify(purchasePriceRequest(form, documentType));
  const documentId = form?.id;
  useEffect(() => {
    const request = JSON.parse(requestKey);
    const previous = previousContext.current;
    previousContext.current = { documentId, businessUnitId, request };
    const repricedKeys = new Set();
    if (previous?.documentId === documentId && previous?.businessUnitId === businessUnitId) {
      const previousLines = new Map(previous.request.lines.map((line) => [line.client_key, line]));
      request.lines.forEach((line) => {
        const oldLine = previousLines.get(line.client_key);
        if (oldLine && (request.date !== previous.request.date || request.currency !== previous.request.currency ||
            JSON.stringify(oldLine) !== JSON.stringify(line))) repricedKeys.add(line.client_key);
      });
    }
    if (!enabled || !businessUnitId || !request.date || !request.lines.length) return undefined;
    const originalPrices = new Map((latestForm.current?.lines || []).filter((line) => positiveId(line.item_id)).map((line, index) => [lineKey(line, index), line.unit_price]));
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await axios.post("/api/purchasing/tenders-pricing/prices", request);
        if (cancelled || !Array.isArray(response.data?.lines)) return;
        setForm((previous) => {
          if (!previous || previous.id !== documentId || JSON.stringify(purchasePriceRequest(previous, documentType)) !== requestKey) return previous;
          return applyPurchasePriceResults(previous, response.data.lines, documentType, originalPrices, repricedKeys);
        });
      } catch {
        // The server resolves and validates the price again when saving the document.
      }
    }, 120);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [businessUnitId, documentId, documentType, enabled, requestKey, setForm]);
}

export function TenderPriceLabel({ line }) {
  const { languageId } = usePmsTheme();
  if (!line?.tender_id && line?.price_source !== "tender") return null;
  const spanish = String(languageId).startsWith("es");
  return <span className="purchTenderPriceLabel" title={[line.tender_starts_on, line.tender_ends_on].filter(Boolean).join(" — ")}>
    {spanish ? "Precio licitado" : "Tendered price"}
  </span>;
}

export default function TenderDeliveryNoteWarning({ isOpen, supplierId, date, currency = "EUR", deliveryNoteId, lines = [] }) {
  const { languageId } = usePmsTheme();
  const spanish = String(languageId).startsWith("es");
  const [comparison, setComparison] = useState({ key: "", differences: [], warnings: [] });
  const requestKey = JSON.stringify({
    supplier_id: positiveId(supplierId), date: String(date || "").slice(0, 10), currency,
    delivery_note_id: positiveId(deliveryNoteId),
    lines: lines.map((line, index) => ({
      client_key: String(index), id: positiveId(line.id), item_id: positiveId(line.item_id),
      po_line_id: positiveId(line.po_line_id), unit_id: positiveId(line.unit_id),
      po_receipts: (line.po_receipts || []).map((receipt) => ({ po_line_id: positiveId(receipt.po_line_id), quantity_received: receipt.quantity_received })),
      unit_level: line.unit_level || "unit", unit_factor: line.unit_factor,
      unit_price_in_dn: line.unit_price_in_dn, quantity_in_dn: line.quantity_in_dn,
      is_free_stock: Boolean(line.is_free_stock),
      price_status: line.price_status,
    })).filter((line) => line.item_id && line.price_status !== "missing" && line.price_status !== "estimated"
      && (Number(line.unit_price_in_dn) > 0 || line.is_free_stock)),
  });
  useEffect(() => {
    const request = JSON.parse(requestKey);
    if (!isOpen || !request.supplier_id || !request.date || !request.lines.length) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await axios.post("/api/purchasing/tenders-pricing/comparisons", request);
        if (!cancelled) setComparison({ key: requestKey, differences: response.data?.differences || [], warnings: response.data?.warnings || [] });
      } catch {
        if (!cancelled) setComparison({ key: requestKey, differences: [] });
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [isOpen, requestKey]);
  const differences = comparison.key === requestKey ? comparison.differences : [];
  const warnings = comparison.key === requestKey ? comparison.warnings || [] : [];
  if (!isOpen || (!differences.length && !warnings.length)) return null;
  const money = (value) => Number(value || 0).toLocaleString(spanish ? "es-ES" : "en-GB", { maximumFractionDigits: 4 });
  return <aside className="purchTenderWarning" role="status" aria-live="polite">
    <strong>{differences.length ? (spanish ? "Diferencias con el precio licitado" : "Differences from tendered prices") : (spanish ? "Comprobación del precio licitado" : "Tender price comparison")}</strong>
    <p>{spanish
      ? "Aviso informativo: puedes guardar y verificar el albarán con su precio real. La comparación utiliza la unidad base de inventario. Las diferencias estarán disponibles en Licitaciones → Reclamaciones."
      : "For information: you can save and verify the delivery note at its actual price. Prices are compared in the base inventory unit. Differences will be available in Tenders → Claims."}</p>
    <ul>{differences.map((entry, index) => <li key={`${entry.client_key || entry.item_id}-${index}`}>
      {entry.item_name || lines[Number(entry.client_key)]?.item?.name || `#${entry.item_id}`}: {spanish ? "licitado" : "tendered"} {money(entry.tender_price)} {entry.currency || currency}, {spanish ? "recibido" : "received"} {money(entry.received_price)} {entry.currency || currency}
      {entry.total_difference != null && <> · {spanish ? "diferencia total" : "total difference"}: {money(entry.total_difference)} {entry.currency || currency}</>}
    </li>)}</ul>
    {warnings.length > 0 && <p>{spanish ? "Hay líneas cuya referencia no se ha podido comparar automáticamente; revisa sus condiciones para la reclamación." : "Some line references could not be compared automatically; review their terms for your claim."}</p>}
  </aside>;
}
